import React, { useState, useEffect } from 'react';
import './PropertyDetails.css';
import { FaArrowLeft, FaEdit } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import { ENDPOINTS } from '../../utilities/endpoints';
import PriceModal from './PriceModal/PriceModal';
import NameModal from './NameModal/NameModal';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store/store';
import { createProperty } from '../../store/reducers/propertiesSlice';
import SellModal from './SellModal/SellModal';
interface HistoryPoint {
  date: string;
  price: number;
}

interface PropertyDetails {
  _id: string;
  name: string;
  buildingId: string;
  ownerId: string;
  description: string;
  image: string;
  price: number;
  noOfFloors: number;
  floorArea: string;
  height: string;
  isBuy: boolean;
  isFirstTime: boolean;
  forSale: boolean;
  color: string;
  location: { lat: number; lng: number };
  status: string;
}

const PropertyDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get('id') || '';
  const paramName = params.get('name') || '';
  const paramPrice = parseFloat(params.get('price') || '0');
  const paramLat = parseFloat(params.get('lat') || '0');
  const paramLng = parseFloat(params.get('lng') || '0');
  const currentUserId = localStorage.getItem('userId') || '';
  const [details, setDetails] = useState<PropertyDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [change, setChange] = useState({ diff: 0, pct: 0 });
  const [offerPrice, setOfferPrice] = useState('');
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [SellPrice, setSellPrice] = useState('');
  const [isSellModalOpen, setIsSellModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoadingDetails(true);
    setDetailsError(null);

    const token = localStorage.getItem('token') || '';
    fetch(`${ENDPOINTS.PROPERTIES.BASE}/checkPropertyDetails?text=${id}`, {
      headers: { Authorization: token, Accept: 'application/json' },
    })
      .then(res => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then(body => {
        const list = body.data.propertyDetails as PropertyDetails[];
        if (Array.isArray(list) && list.length > 0) {
          setDetails(list[0]);
        }
      })
      .catch(err => {
        console.error(err);
        setDetailsError(err.message);
      })
      .finally(() => setLoadingDetails(false));
  }, [id]);

  const isForSale = details?.forSale === true;


  useEffect(() => {
    if (!id) return;
    setLoadingHistory(true);
    setHistoryError(null);

    const token = localStorage.getItem('token') || '';
    fetch(ENDPOINTS.PROPERTIES.PRICE_HISTORY(id), {
      headers: { Authorization: token, Accept: 'application/json' },
    })
      .then(res => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then(body => {
        const pts = body.data.priceHistory.map((b: any) => ({
          date: new Date(b.insertDate * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          price: b.bidAmount,
        }));
        setHistory(pts);
        if (pts.length >= 2) {
          const first = pts[0].price;
          const last = pts[pts.length - 1].price;
          const diff = last - first;
          const pct = first > 0 ? (diff / first) * 100 : 0;
          setChange({ diff, pct });
        }
      })
      .catch(err => {
        console.error(err);
        setHistoryError(err.message);
      })
      .finally(() => setLoadingHistory(false));
  }, [id]);

  const fmtNum = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
  const fmtPct = (n: number) => `(${n >= 0 ? '+' : ''}${n.toFixed(2)}%)`;

  const openBuyModal = () => { setOfferPrice(''); setIsBuyModalOpen(true); };
  const closeBuyModal = () => setIsBuyModalOpen(false);
  const openSellModal = () => { setSellPrice(''); setIsSellModalOpen(true); };
  const closeSellModal = () => setIsSellModalOpen(false);

  const handleBuySubmit = () => {
    setIsBuyModalOpen(false);

    const priceNum = parseFloat(offerPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      alert('Please enter a valid price');
      return;
    }

    const buildingName = details?.name ?? paramName;
    const lat = details?.location.lat ?? paramLat;
    const lng = details?.location.lng ?? paramLng;


    const processingFee = 6.00;
    const salesTax = 2.99;
    const total = priceNum + processingFee + salesTax;

    navigate('/order-review', {
      state: {
        buildingName,
        price: priceNum,
        processingFee,
        salesTax,
        total,
        propertyId: id,
        lat,
        lng
      }
    });
  };

  const handleSellSubmit = () => {
    setIsBuyModalOpen(false);

    const SellNum = parseFloat(SellPrice);
    if (isNaN(SellNum) || SellNum <= 0) {
      alert('Please enter a valid price');
      return;
    }

    const buildingName = details?.name ?? paramName;
    const lat = details?.location.lat ?? paramLat;
    const lng = details?.location.lng ?? paramLng;


    const processingFee = 5.00;
    const salesTax = 2.99;
    const total = SellNum + processingFee;
    if (!details?._id) {
      alert('Missing property ID');
      return;
    }
    navigate('/sell-review', {
      state: {
        buildingName,
        price: SellNum,
        processingFee,
        salesTax,
        total,
        propertyId: details._id,
        lat,
        lng
      }
    });
  };
  const handleNonOwnerClick = () => {
   if (details?.forSale) {
     const buildingName  = details.name;
     const price         = details.price;
     const processingFee = 6;         
     const salesTax      = 2.99;       
     const total         = price + processingFee + salesTax;
     const lat           = details.location.lat;
     const lng           = details.location.lng;

     navigate('/bid-review', {
       state: {
         buildingName,
         price,
         salesTax,
         processingFee,
         propertyId: details?._id,
         lat,
         lng
       }
     });
   } else {
     openBuyModal();
   }
 };
  const isOwner = details?.ownerId === currentUserId;
  const openNameModal = () => {
    setNewName(details?.name ?? paramName);
    setIsNameModalOpen(true);
  };
  const closeNameModal = () => setIsNameModalOpen(false);
  const handleNameSubmit = (name: string) => {
    const lat = details?.location.lat ?? paramLat;
    const lng = details?.location.lng ?? paramLng;

    dispatch(createProperty({
      buildingId: id,
      name,
      location: { lat, lng }
    }))
      .unwrap()
      .then(prop => {
        setDetails(d => d ? { ...d, name: prop.name } : d);
        setIsNameModalOpen(false);
      })
      .catch(err => {
        console.error('Failed to update name:', err);
      });
  };

  const displayName = details?.name ?? paramName;
  const displayPrice = details?.price.toFixed(2) ?? paramPrice.toString();
  const displayLat = (details?.location.lat ?? paramLat).toString();
  const displayLng = (details?.location.lng ?? paramLng).toString();

  return (
    <div className="property-details-container">
      <div className="property-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>

        <h2 className="header-title">Property Details</h2>

        {isOwner && (
          <FaEdit
            className="edit-button"
            onClick={openNameModal}
            color="#2e90fa"
            size={24}
          />
        )}
      </div>

      <div className="property-header-row">
        <div className="property-title-block">
          <h1 className="property-name">{displayName}</h1>
          <h2 className="property-price">${displayPrice}</h2>
        </div>
        {history.length >= 2 && (
          <div
            className="change-block"
            style={{ color: change.diff >= 0 ? '#4CAF50' : '#F44336' }}
          >
            <span className="change-indicator">
              {fmtNum(change.diff)}{' '}
              <span className="pct">{fmtPct(change.pct)}</span>
            </span>
          </div>
        )}
      </div>

      <div className="property-stats">
        <div><strong>Latitude</strong><p>{displayLat}</p></div>
        <div><strong>Longitude</strong><p>{displayLng}</p></div>
      </div>

      <div className="chart-section">
        <strong>Price history</strong>
        {loadingHistory ? (
          <p>Loading history…</p>
        ) : historyError ? (
          <p style={{ color: 'red' }}>Error: {historyError}</p>
        ) : (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={history}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4c74d9" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#4c74d9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#888" />
                <YAxis stroke="#888" tickFormatter={v => `$${v}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #eee',
                    fontSize: '14px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                  formatter={(val: number) => `$${val.toLocaleString()}`}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke="#4c74d9"
                  fillOpacity={1}
                  fill="url(#colorPrice)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {isForSale ? (
        <button className="buy-button" onClick={handleNonOwnerClick}>
          Create a bid
        </button>
      ) : isOwner ? (
        <button className="buy-button" onClick={openSellModal}>
          Sell or Ask
        </button>
      ) : !details?.ownerId ? (
        <button className="buy-button" onClick={handleNonOwnerClick}>
          Buy or Bid
        </button>
      ) : null}

      <PriceModal
        isOpen={isBuyModalOpen}
        value={offerPrice}
        onChange={setOfferPrice}
        onCancel={closeBuyModal}
        onSubmit={handleBuySubmit}
      />

      <SellModal
        isOpen={isSellModalOpen}
        value={SellPrice}
        onChange={setSellPrice}
        onCancel={closeSellModal}
        onSubmit={handleSellSubmit}
      />

      <NameModal
        isOpen={isNameModalOpen}
        value={newName}
        onChange={setNewName}
        onCancel={closeNameModal}
        onSubmit={handleNameSubmit}
      />

      {loadingDetails && <p>Loading property details…</p>}
      {detailsError && <p style={{ color: 'red' }}>Error: {detailsError}</p>}
    </div>
  );
};

export default PropertyDetails;
